import React from 'react';
import { useSlotCanvasStore } from '@/store/slotCanvasStore';
import { cn } from '@/lib/utils';
import { PANEL_HEADER_CLASS, PanelHeaderTitle } from '@/components/common/panelChrome';

export interface SlotCanvasLayoutProps {
  title: string;
  subtitle?: string;
  /**
   * Optional action cluster rendered in the header row, right-aligned.
   * Use for slot-specific actions (Schedule report, Save, …). Space is tight
   * at narrow canvas widths — prefer a single primary button plus a
   * "more actions" dropdown over three full-width buttons.
   */
  headerActions?: React.ReactNode;
  /**
   * Overrides the body's default `overflow-auto`. Slots whose content owns
   * its own scrolling (iframes, code viewers) pass `overflow-hidden` so the
   * outer body doesn't double-scroll.
   */
  bodyClassName?: string;
  children: React.ReactNode;
}

/**
 * Shared layout for slot bodies rendered inside the canvas frame.
 *
 * The header is the panel header every right-side panel carries (the
 * artifact's `.phead`, via {@link @/components/common/panelChrome}): a 52px
 * strip at 18px padding holding the title — or, when this slot was opened over
 * another, the breadcrumb back to it — then the slot's own actions. The
 * frame's own chrome (expand, collapse/close) is absolutely positioned into
 * the same strip, so the right padding here is the room it needs.
 *
 * Body: scrollable by default; see {@link SlotCanvasLayoutProps.bodyClassName}.
 */
export function SlotCanvasLayout({
  title,
  subtitle,
  headerActions,
  bodyClassName,
  children,
}: SlotCanvasLayoutProps) {
  const returnSlotId = useSlotCanvasStore((s) => s.returnSlotId);
  const goBack = useSlotCanvasStore((s) => s.goBack);
  // The slot this one was opened over, named as the breadcrumb's first half.
  // Its registry entry is what the navigator calls it, so the crumb and the
  // navigator agree on what the reader would be going back to.
  const returnTitle = useSlotCanvasStore((s) =>
    s.returnSlotId ? s.registry[s.returnSlotId]?.title : undefined,
  );

  return (
    <div className="flex flex-col h-full w-full">
      {/* pr-[90px] is the frame's chrome cluster — two 28px squares, 8px
          apart, 18px off the right edge — so slot headerActions never sit
          under it. */}
      <div className={cn(PANEL_HEADER_CLASS, 'pr-[90px]')}>
        <PanelHeaderTitle
          title={title}
          subtitle={subtitle}
          parent={returnSlotId && returnTitle
            ? { label: returnTitle, onBack: () => goBack() }
            : undefined}
        />
        {headerActions && (
          <div className="flex items-center gap-1 shrink-0">
            {headerActions}
          </div>
        )}
      </div>
      <div className={`flex-1 min-h-0 ${bodyClassName ?? 'overflow-auto'}`}>{children}</div>
    </div>
  );
}
